import React, { Suspense, useCallback, useEffect } from 'react';
import Button from 'react-bootstrap/Button';
import { useTranslation } from 'react-i18next';
import { FaExternalLinkAlt } from 'react-icons/fa';
import styled from 'styled-components';

import { uiConstants } from '../../constants';
import { bsTiny, useNavigateCCASS } from '../../helpers/ui.helper';
import BlurPlaceholder from '../../modules/FreeUser/BlurPlaceholder';
import FeatureRequiresVIPAlert from '../../modules/FreeUser/FeatureRequiresVIPAlert';
import CCASSTables from '../../modules/Insight/CCASS/CCASSTables';
import HKEXNewsViewer from '../../modules/Insight/CCASS/HKEXNewsViewer';
import { Region } from '../../shared/types';
import { SignalOccurrence } from '../../types';
import LoadingImage from '../general/LoadingImage';
import StockShortkeys from '../shortkey/StockShortkeys';
import { regionToCategory } from '../stock/stockCategory';

const PriceViewerLazy = React.lazy(() => import('../price-viewer/PriceViewer'));

const Styles = styled.div`
  position: relative;
  .price-viewer {
    text-align: left;
    > .row {
      margin-left: 0;
      margin-right: 0;
    }
  }
  .ccass-viewer,
  .news-viewer {
    overflow-y: auto;
    padding: 1rem;
    padding-left: 1.5rem;
    padding-right: 1.5rem;
    table {
      border-collapse: collapse;
      display: table;
    }
  }
  .ccass-viewer {
    max-height: 600px;
  }
  .news-viewer {
    max-height: 600px;
  }
  .shadow-mask {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    box-shadow: inset 0 0 5px 0 #888888;
    pointer-events: none;
  }
`;

interface Props {
  stockCode: string;
  onNavigateCCASS?: (stockCode: string) => void;
  mode?: number;
  onShowDailyPrice?: (stockCode: string) => void;
  probabilityData?: any;
  signalOccurrences?: SignalOccurrence[];
  region: Region;
  influencerId?: number;
  when?: string;
}

const DLStockPeek: React.FC<Props> = React.memo(function DLStockPeek({
  stockCode,
  onNavigateCCASS = null,
  mode = 10,
  onShowDailyPrice,
  probabilityData = null,
  signalOccurrences = null,
  region,
  influencerId,
  when,
}) {
  const { t } = useTranslation();

  useEffect(() => {
    if (mode === uiConstants.MODE_DAILY_PRICE && onShowDailyPrice) {
      onShowDailyPrice(stockCode);
    }
  });

  const defaultHandleNavigateCCASS = useNavigateCCASS();
  const handleNavigateCCASS = useCallback(() => {
    onNavigateCCASS
      ? onNavigateCCASS(stockCode)
      : defaultHandleNavigateCCASS(stockCode, when);
  }, [defaultHandleNavigateCCASS, onNavigateCCASS, stockCode, when]);

  return (
    <Styles>
      {mode === uiConstants.MODE_DAILY_PRICE && (
        <div className="price-viewer">
          <BlurPlaceholder featureKey="price-viewer">
            <Suspense fallback={<LoadingImage loading displayAsBlock />}>
              <PriceViewerLazy
                stockCode={stockCode}
                probabilityData={probabilityData}
                signalOccurrences={signalOccurrences}
                influencerId={influencerId}
              />
            </Suspense>
          </BlurPlaceholder>
          <FeatureRequiresVIPAlert featureTitle={t('feature-PriceViewer')} />
        </div>
      )}
      {mode === uiConstants.MODE_CCASS_SUMMARY && (
        <div className="ccass-viewer">
          <BlurPlaceholder featureKey="ccass-summary">
            <div>
              <Button
                variant="link"
                size={bsTiny}
                onClick={handleNavigateCCASS}
              >
                <FaExternalLinkAlt /> {t('view-full-page')}
              </Button>
            </div>
            <CCASSTables stockCode={stockCode} date={when} />
          </BlurPlaceholder>
          <FeatureRequiresVIPAlert featureTitle={t('feature-CCASSTable')} />
        </div>
      )}
      {mode === uiConstants.MODE_CCASS_NEWS && (
        <div className="news-viewer">
          <BlurPlaceholder featureKey="hkex-news">
            <HKEXNewsViewer stockCode={stockCode} />
          </BlurPlaceholder>
          <FeatureRequiresVIPAlert featureTitle={t('feature-HKEXNewsViewer')} />
        </div>
      )}
      <div className="shadow-mask" />
      <StockShortkeys ticker={stockCode} category={regionToCategory(region)} />
    </Styles>
  );
});

export default DLStockPeek;
